// 덴클 케어(분리 환자앱) 1차 — 치과 웹이 부르는 서버 호출·조회 한 곳 (2026-10-04 계획_환자앱1차_통합 W1·W3)
//
// 서버 계약: functions/lib/patient-app-gate.js(dev-server 10-04 구현, 미배포)와 대조함. 이름이 바뀌면 FN 표만, 응답 키가 바뀌면
//    normalizeConsentState 만 고친다. 화면 컴포넌트는 CF 이름·응답 형태를 직접 알지 않는다.
//   accept 오류: permission-denied {code:'owner-only'} / failed-precondition {code:'consent-mismatch'|'delegation-not-ready'}
//   linkOrderToPatient 오류: failed-precondition {code:'care-gate-closed'}(위탁 확인 없음·철회·파일럿 밖) / {code:'app-link-check-required'}(L-6 확인 시각 없음·3시간 지남)
//
// 가정 A (S4 ① 위탁 확인 조회, 치과 대표·직원):
//   getPatientAppClinicConsent({}) → { confirmed, revoked, version, at(ms), revokedAt(ms), ready(false=서버 정본 표 비어 있음), purgeAt(ms — 철회 후 앱 등록 정보 삭제 예정 시각, 없으면 revokedAt+7일), current, currentTextHash, isCurrent, canConfirm(대표만), isPilot(불리언 — 서버 추가 예정 10-04, true 일 때만 위탁 확인 카드 노출) }
//   철회 규칙 = 정본 v2 §2-5(철회 즉시 게이트 닫힘, 7일 뒤 앱 등록 레코드만 삭제, 7일 안 재확인 시 삭제 취소)
// 가정 B (S4 ① 위탁 확인 기록, 대표만 — 직원 = permission-denied):
//   acceptPatientAppClinicConsent({ version, textHash }) → { ok, version, already }
// 가정 C (S4 ② 철회, 대표만):
//   revokePatientAppClinicConsent({}) → { ok, already }
// 공통: region asia-northeast3, onCall. 서버 미배포(not-found·internal 이고 details 없음) → "서버 준비 중" 문구.
//
// 가정 D (새 앱 등록 표시 — 새 CF 없음): 명부 clinicPatientRecords/{치과 uid}/patients 직접 list(rules 오너·직원 허용, 기존 경로).
//   대표 결정(10-04) "앱 등록 즉시 성립" → 앱 등록 레코드는 처음부터 active. 판별은 clinicPatientsApi.isAppRegisteredRecord 한 곳.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, limit, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { normalizeRecord, isAppRegisteredRecord, deleteClinicPatient } from './clinicPatientsApi';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

// 서버 함수 이름(가칭) — 확정되면 여기만 바꾼다
export const CARE_FN = {
    getClinicConsent: 'getPatientAppClinicConsent',
    acceptClinicConsent: 'acceptPatientAppClinicConsent',
    revokeClinicConsent: 'revokePatientAppClinicConsent',
};

const toMs = (v) => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v._seconds === 'number') return v._seconds * 1000;
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : null;
};

// 응답 정규화 — 판정 불가 값은 false(fail-closed)
export function normalizeConsentState(r) {
    const d = r || {};
    return {
        confirmed: d.confirmed === true && d.revoked !== true,
        revoked: d.revoked === true,
        version: typeof d.version === 'string' ? d.version : '',
        at: toMs(d.at),
        revokedAt: toMs(d.revokedAt),
        purgeAt: toMs(d.purgeAt) ?? (toMs(d.revokedAt) != null ? toMs(d.revokedAt) + 7 * 24 * 3600 * 1000 : null),
        current: typeof d.current === 'string' ? d.current : '',
        currentTextHash: typeof d.currentTextHash === 'string' ? d.currentTextHash : '',
        isCurrent: d.isCurrent === true,
        ready: d.ready !== false,   // 키가 없으면(구 서버) 판정은 current 비교에 맡긴다
        canConfirm: d.canConfirm === true,
        isPilot: d.isPilot === true,   // 위탁 확인 카드 노출 가드 — 정규화에서 빠져 있어 카드가 늘 숨던 문제 수정(10-05)
    };
}

export async function getPatientAppClinicConsent() {
    const res = await call(CARE_FN.getClinicConsent)({});
    return normalizeConsentState(res?.data);
}

// ── 공유 조회 (2026-10-06 로드맵 파일럿 게이트) — 한 화면에서 위탁 확인 카드(CareAppClinicConsentCard)와 useRoadmapPilot 훅이 같이 부르면
//    네트워크 호출은 1회만 나가게 진행 중 Promise 와 결과를 짧게(30초) 공유한다. 확인·철회 뒤에는 invalidate 로 비우고 구독자(훅)에 알린다.
//    실패 결과는 캐시하지 않는다(다음 호출이 다시 시도).
const CONSENT_SHARE_MS = 30 * 1000;
let consentShared = { promise: null, data: null, at: 0 };
const consentListeners = new Set();

export function getPatientAppClinicConsentShared({ force = false } = {}) {
    const now = Date.now();
    if (!force && consentShared.data && now - consentShared.at < CONSENT_SHARE_MS) return Promise.resolve(consentShared.data);
    if (!force && consentShared.promise) return consentShared.promise;
    const p = getPatientAppClinicConsent()
        .then(d => { consentShared = { promise: null, data: d, at: Date.now() }; return d; })
        .catch(e => { consentShared = { promise: null, data: null, at: 0 }; throw e; });
    consentShared.promise = p;
    return p;
}

// 확인·철회 직후 호출 — 캐시를 비우고 구독자에게 재조회를 알린다(훅이 즉시 노출 상태를 갱신)
export function invalidatePatientAppClinicConsent() {
    consentShared = { promise: null, data: null, at: 0 };
    consentListeners.forEach(fn => { try { fn(); } catch (e) { console.error('[careAppApi] consent listener 실패:', e?.message || e); } });
}

export function subscribePatientAppClinicConsent(fn) {
    consentListeners.add(fn);
    return () => consentListeners.delete(fn);
}

export async function acceptPatientAppClinicConsent(version, textHash) {
    const res = await call(CARE_FN.acceptClinicConsent)({ version: String(version || ''), textHash: String(textHash || '') });
    return res?.data || {};
}

export async function revokePatientAppClinicConsent() {
    const res = await call(CARE_FN.revokeClinicConsent)({});
    return res?.data || {};
}

// 의뢰서 연결 거부(care-gate-closed) — 치과 처리위탁 확인이 없거나 철회됐거나 파일럿 밖
export const CARE_GATE_CLOSED_TEXT = '환자 앱 등록 처리위탁 확인이 유효하지 않아 앱 등록 환자에게 연결할 수 없습니다. 치과 대표가 환자 관리 화면에서 처리위탁 확인 상태를 확인해 주세요.';

export const CARE_SERVER_NOT_READY = '서버 기능이 아직 준비되지 않았습니다. 준비되면 이 화면에서 바로 쓸 수 있습니다.';

// 서버 미배포 판정 — details 없는 not-found/internal (receptionApi 와 같은 관례)
export function isCareServerNotReady(e) {
    const code = String(e?.code || '').replace(/^functions\//, '');
    return (code === 'not-found' || code === 'internal') && !e?.details?.code;
}

// 오류 → 화면 문구. 원본은 코드만 콘솔에(환자 정보 없음)
// 정본 v2.6 §4-1(법적보안 10-05): 권한 거부 문구를 상황별로 나눈다.
//   위탁 확인 카드(기본, 정본 L-1) = '대표 계정만 확인할 수 있습니다.'
//   「우리 환자 아님 — 삭제」(ClinicPatients, opts.action === 'delete') = '이 계정으로는 삭제할 수 없습니다. 치과 대표에게 요청해 주세요.'
export const CARE_OWNER_ONLY_CONFIRM_TEXT = '대표 계정만 확인할 수 있습니다.';
export const CARE_DELETE_DENIED_TEXT = '이 계정으로는 삭제할 수 없습니다. 치과 대표에게 요청해 주세요.';
export function careClinicError(e, fallback = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.', opts = {}) {
    console.error('[careAppApi]', e?.code || '', e?.details?.code || '', e?.message || e);
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    if (isCareServerNotReady(e)) return CARE_SERVER_NOT_READY;
    const code = String(e?.code || '').replace(/^functions\//, '');
    if (code === 'unauthenticated') return '로그인이 필요합니다.';
    const detail = e?.details?.code;
    if (detail === 'owner-only' || code === 'permission-denied') return opts.action === 'delete' ? CARE_DELETE_DENIED_TEXT : CARE_OWNER_ONLY_CONFIRM_TEXT;
    if (detail === 'consent-mismatch') return '확인 내용이 최신이 아닙니다. 화면을 새로고침해 주세요.';
    if (detail === 'delegation-not-ready') return '확인 문서가 아직 준비되지 않았습니다. 준비되면 이 화면에서 확인할 수 있습니다.';
    if (detail === 'care-gate-closed') return CARE_GATE_CLOSED_TEXT;
    if (code === 'resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    return fallback;
}

// ── 새 앱 등록(최근 N일) — 「새 앱 등록 N명」 표시용(W3). where 필터 필수(전체 스캔 금지): createdAt 범위 + 상한 200.
//   createdAt 은 서버 serverTimestamp(Timestamp). 단일 필드 범위라 복합 색인 불필요. 앱 등록 판별·deleted 제외는 메모리에서.
//   환자명은 반환값에만 — 콘솔 기록 금지.
export const CARE_NEW_REG_DAYS = 7;
export async function listRecentAppRegistrations(clinicId, days = CARE_NEW_REG_DAYS) {
    if (!clinicId) return [];
    const since = Timestamp.fromMillis(Date.now() - days * 24 * 3600 * 1000);
    const snap = await getDocs(query(
        collection(db, 'clinicPatientRecords', clinicId, 'patients'),
        where('createdAt', '>=', since), orderBy('createdAt', 'desc'), limit(200),
    ));
    return snap.docs.map(d => normalizeRecord(d.id, d.data()))
        .filter(r => r.status !== 'deleted' && isAppRegisteredRecord(r));
}

// ── 「우리 환자 아님 — 삭제」(정본 v2 §9 L-7) — 기존 deleteClinicPatient 재사용, 사유 = 아래 상수.
//   2026-10-05 대표 "권고대로": 이 사유에 한해 직원도 허용(서버 clinic-patients.js 변경 진행 중 — 미배포 동안 직원은 permission-denied).
//   다른 삭제 사유는 대표 전용 유지.
export const APP_REG_DELETE_REASON = 'not_our_patient';
export async function deleteAppRegistration(pid) {
    return deleteClinicPatient(pid, APP_REG_DELETE_REASON);
}

// ── 의뢰서 연결 확인(정본 v2 §9 L-6) 기록 — linkOrderToPatient({ orderId, pid, appLinkCheck:number(ms) }).
//   서버가 접속기록(누가·언제)에 reason 'app_link_checked:{ms}' 로 남긴다.
//   서버 최종 계약(10-04): 케어 레코드 연결은 appLinkCheck 필수, 허용 창 3시간(clinic-patients.js APP_LINK_CHECK_MAX_AGE_MS) —
//   없거나 지나면 failed-precondition {code:'app-link-check-required'}(차단).
export const buildAppLinkCheck = (confirmedAtMs) => Math.trunc(Number(confirmedAtMs) || Date.now());
export const APP_LINK_CHECK_MAX_AGE_MS = 3 * 3600 * 1000;
// 저장 직전 재확인 기준 — 서버 창(3시간)보다 30분 짧게: 확인 뒤 사진·파일 업로드가 길어져도 서버 도착 전에 창이 닫히지 않게
export const APP_LINK_CHECK_REPROMPT_MS = APP_LINK_CHECK_MAX_AGE_MS - 30 * 60 * 1000;
export const isAppLinkCheckStale = (checkedAtMs, nowMs = Date.now()) =>
    !Number.isFinite(Number(checkedAtMs)) || nowMs - Number(checkedAtMs) > APP_LINK_CHECK_REPROMPT_MS;
